<!--
  Fixed header: sizes itself, so the list below can take exactly the remaining height.

  `rounded-t-xl` replaces the card's former `overflow-hidden` (#534): it is the only thing that clipping did
  visually, and the clipping would also have cut off the "Anzeigeoptionen" panel opened from `[card-actions]`.
-->
<div class="relative flex shrink-0 items-center gap-x-4 rounded-t-xl border-b border-gray-900/5 bg-gray-50 p-4">
  <ng-content select="[card-icon]"/>
  <div class="min-w-0 truncate text-sm font-medium leading-6 text-gray-900">{{ label() }}</div>
  <!--
    Right-aligned header actions (#534: the "Kombinationen einzeln" toggle on the "Artikel" card).

    `ml-auto` lives HERE rather than on the swap button, so the slot and the button stay side by side at the
    right edge instead of each claiming half the free space; the button keeps its own `shrink-0`, and the
    label's `min-w-0 truncate` is what gives way on a 360px phone.

    `stopPropagation` is belt-and-braces: the collapse/advance handler is currently on the swap button itself,
    not on this header row, and the settings gear already stops its own click — but a projected control must
    not start firing an ancestor's handler the day this header becomes clickable.
  -->
  <div class="ml-auto flex shrink-0 items-center gap-x-2" (click)="$event.stopPropagation()">
    <ng-content select="[card-actions]"/>
  </div>
  <button type="button" (click)="next.emit()"
          aria-label="Nächste Übersicht anzeigen"
          class="flex shrink-0 cursor-pointer text-gray-600 transition-colors hover:text-gray-800
                 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1
                 focus-visible:outline-theme-primary-500 lg:hidden">
    <svg class="bi bi-arrow-left-right" height="20" viewBox="0 0 16 16" width="20" aria-hidden="true"
         xmlns="http://www.w3.org/2000/svg">
      <path
        d="M1 11.5a.5.5 0 0 0 .5.5h11.793l-3.147 3.146a.5.5 0 0 0 .708.708l4-4a.5.5 0 0 0 0-.708l-4-4a.5.5 0 0 0-.708.708L13.293 11H1.5a.5.5 0 0 0-.5.5zm14-7a.5.5 0 0 1-.5.5H2.707l3.147 3.146a.5.5 0 1 1-.708.708l-4-4a.5.5 0 0 1 0-.708l4-4a.5.5 0 1 1 .708.708L2.707 4H14.5a.5.5 0 0 1 .5.5z"
        fill-rule="evenodd"/>
    </svg>
  </button>

  <!--
    #716 / Task #722 — "the rows below are still settling".

    Absolutely positioned on the header's bottom edge, so it costs NO layout at any width: the 360px header
    (label + gear + swap button, see the `[card-actions]` spec) is byte-identical to its resting state, which a
    pill or a label beside the header text could not have been.

    `animate-pulse` rather than a hand-written travelling bar: it is the idiom this app already uses for
    "something is still loading" (`cash-points`, `legal/settings`), so this introduces no new visual language.
    `motion-reduce:animate-none` leaves the line VISIBLE but still — the affordance survives without motion
    instead of disappearing for the users who asked for less of it.

    `aria-hidden`: the accessible signal is the `aria-busy` on the list itself, below. A second announcement of
    the same fact is noise, and this element has no text to announce anyway.
  -->
  @if (settling()) {
    <span data-testid="event-summary-settling" aria-hidden="true"
          class="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 animate-pulse bg-theme-primary-500
                 motion-reduce:animate-none"></span>
  }
</div>

<!--
  The scroll area. `flex-1 min-h-0` takes the card's remaining height (min-h-0 is what actually lets a flex
  item shrink below its content and scroll); `max-h-72` caps how tall a long list may grow the card. No
  percentage height and no bottom padding standing in for one — see the component doc for #231.

  `tabindex="0"` + a name: a scrollable region whose rows are not themselves focusable (Sonstiges,
  Transaktionstypen, Artikel — only Verkaufspunkt projects links) is otherwise unreachable by keyboard
  (axe `scrollable-region-focusable`).

  `aria-busy` while {@link settling}: this list — NOT the card, and not the header label, which stays exact —
  is the region whose content is about to be replaced. It is an attribute binding that answers `null` at rest,
  so a resting card carries no `aria-busy="false"` noise.

  The rows are deliberately NOT dimmed. An `opacity-60` on this list was tried first and rejected: these rows
  are `text-gray-500` on white (4.8:1), and blending them at 60% drops them to ≈2.4:1 — below AA, for every
  viewer, for the whole round trip. A cue that makes the numbers harder to read is the wrong trade when the
  complaint being answered is "the numbers look wrong": the line above says an update is running, and the
  numbers stay exactly as legible as they were.
-->
<dl data-testid="event-summary-list" tabindex="0" [attr.aria-label]="label()"
    [attr.aria-busy]="settling() ? 'true' : null"
    class="min-h-0 max-h-72 flex-1 divide-y divide-gray-100 overflow-y-auto px-6 py-4 text-sm leading-6
           focus-visible:outline focus-visible:-outline-offset-2 focus-visible:outline-2
           focus-visible:outline-theme-primary-500">
  <ng-content/>
</dl>
